// Smart Sash Admin Panel - Stream Viewer Page

import { Alert, Spinner } from '@/components/common';
import {
  AudioStreamInfo,
  StreamControls,
  StreamPlayer,
  StreamSidebarSection,
  VideoStreamInfo,
} from '@/components/stream';
import { useStreamViewer } from '@/hooks/useStreamViewer';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

function useElapsedTime(startedAt: string | undefined): string {
  const [elapsed, setElapsed] = useState('00:00:00');

  useEffect(() => {
    if (!startedAt) return;

    const tick = () => {
      const diff = Math.max(0, Date.now() - new Date(startedAt).getTime());
      const h = Math.floor(diff / 3_600_000);
      const m = Math.floor((diff % 3_600_000) / 60_000);
      const s = Math.floor((diff % 60_000) / 1_000);
      setElapsed([h, m, s].map((n) => String(n).padStart(2, '0')).join(':'));
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  return elapsed;
}

const LABEL_STYLE: React.CSSProperties = {
  fontSize: '0.65rem',
  textTransform: 'uppercase',
  letterSpacing: '0.07em',
  marginBottom: '2px',
};

const SidebarRow: React.FC<{ label: string; icon: string; children: React.ReactNode }> = ({
  label,
  icon,
  children,
}) => (
  <div>
    <p className="text-muted mb-1" style={LABEL_STYLE}>
      {label}
    </p>
    <span className="d-flex align-items-start gap-2 small">
      <i className={`${icon} text-muted mt-1 flex-shrink-0`} style={{ width: '12px' }} />
      <span className="text-break">{children}</span>
    </span>
  </div>
);

const TechId: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <p className="text-muted mb-1" style={LABEL_STYLE}>
      {label}
    </p>
    <code className="user-select-all text-break" style={{ fontSize: '0.68rem' }}>
      {value}
    </code>
  </div>
);

const formatBytes = (value: number | undefined): string => {
  if (!value) return 'n/a';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const formatBitrate = (value: number | undefined): string => {
  if (!value) return 'n/a';
  if (value < 1_000_000) return `${Math.round(value / 1000)} kbps`;
  return `${(value / 1_000_000).toFixed(1)} Mbps`;
};

const formatMilliseconds = (value: number | undefined): string => {
  if (!value) return 'n/a';
  return `${Math.round(value * 1000)} ms`;
};

const formatResolution = (videoInfo: VideoStreamInfo): string => {
  const width =
    videoInfo.stats?.frameWidth || videoInfo.dimensions?.width || videoInfo.mediaSettings?.width;
  const height =
    videoInfo.stats?.frameHeight || videoInfo.dimensions?.height || videoInfo.mediaSettings?.height;

  return width && height ? `${width} x ${height}` : 'n/a';
};

export const StreamViewer: React.FC = () => {
  const {
    activeSession,
    connectionInfo,
    isJoining,
    viewerStatus,
    isMuted,
    volume,
    isFullscreen,
    videoContainerRef,
    handleLeave,
    handleDisconnected,
    handleConnected,
    toggleMute,
    handleVolumeChange,
    toggleFullscreen,
  } = useStreamViewer();

  const elapsed = useElapsedTime(activeSession?.startedAt);
  const [videoInfo, setVideoInfo] = useState<VideoStreamInfo | null>(null);
  const [audioInfo, setAudioInfo] = useState<AudioStreamInfo | null>(null);

  if (!activeSession) {
    return (
      <div className="container-fluid py-4">
        <Link to="/streams" className="btn btn-link text-decoration-none p-0 mb-3">
          <i className="fas fa-arrow-left me-2" />
          Back to Streams
        </Link>
        <div className="d-flex justify-content-center py-5">
          <Spinner />
        </div>
      </div>
    );
  }

  if (viewerStatus === 'error' && !connectionInfo) {
    return (
      <div className="container-fluid py-4">
        <Link to="/streams" className="btn btn-link text-decoration-none p-0 mb-3">
          <i className="fas fa-arrow-left me-2" />
          Back to Streams
        </Link>
        <Alert variant="danger">
          <i className="fas fa-exclamation-circle me-2" />
          Failed to join the session. The session may have ended or you may not have permission.
        </Alert>
      </div>
    );
  }

  return (
    <div
      className="d-flex flex-column"
      style={{ height: 'calc(100vh - 56px)', overflow: 'hidden' }}
    >
      {/* Header bar */}
      <div
        className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom flex-shrink-0"
        style={{ background: 'var(--ss-bg-secondary)' }}
      >
        <Link
          to="/streams"
          className="btn btn-sm btn-link text-decoration-none p-0 d-flex align-items-center gap-2"
        >
          <i className="fas fa-arrow-left" />
          <span className="d-none d-sm-inline">Back to Streams</span>
        </Link>

        <div className="text-truncate mx-3 fw-semibold" style={{ maxWidth: '50%' }}>
          <i className="fas fa-video me-2 text-muted" />
          {activeSession.streamTitle || 'Untitled Stream'}
        </div>

        <div style={{ width: '72px' }} />
      </div>

      {/* Two-column body */}
      <div className="d-flex flex-grow-1 overflow-hidden" style={{ minHeight: 0 }}>
        {/* Left: video */}
        <div
          ref={videoContainerRef}
          className="bg-dark d-flex flex-column flex-grow-1 overflow-hidden"
        >
          {/* Video area — centers the 16:9 box in the available space */}
          <div className="flex-grow-1 d-flex align-items-center justify-content-center position-relative overflow-hidden">
            {(isJoining || viewerStatus === 'loading' || viewerStatus === 'connecting') &&
              !connectionInfo && (
                <div className="text-white text-center">
                  <Spinner />
                  <p className="mt-3 mb-0 text-muted">Joining session...</p>
                </div>
              )}

            {connectionInfo?.success && (
              <div style={{ height: '100%', aspectRatio: '16/9', maxWidth: '100%' }}>
                <StreamPlayer
                  wsUrl={connectionInfo.wsUrl}
                  token={connectionInfo.subscribeToken}
                  isMuted={isMuted}
                  volume={volume}
                  onConnected={handleConnected}
                  onDisconnected={handleDisconnected}
                  onVideoInfoChange={setVideoInfo}
                  onAudioInfoChange={setAudioInfo}
                />
              </div>
            )}

            {viewerStatus === 'reconnecting' && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{ background: 'rgba(0,0,0,0.6)', zIndex: 20 }}
              >
                <div className="text-center text-white">
                  <Spinner />
                  <p className="mt-3 mb-0">Reconnecting...</p>
                </div>
              </div>
            )}
          </div>

          {/* Controls bar */}
          <div
            className="flex-shrink-0 px-2 py-2"
            style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
          >
            <StreamControls
              isMuted={isMuted}
              volume={volume}
              isFullscreen={isFullscreen}
              viewerStatus={viewerStatus}
              onToggleMute={toggleMute}
              onVolumeChange={handleVolumeChange}
              onToggleFullscreen={toggleFullscreen}
              onLeave={handleLeave}
            />
          </div>
        </div>

        {/* Right: info sidebar */}
        <div
          className="d-none d-lg-flex flex-column border-start overflow-y-auto flex-shrink-0"
          style={{ width: '300px', background: 'var(--ss-bg-secondary)' }}
        >
          {/* Live status header */}
          <div className="p-3 border-bottom d-flex align-items-center justify-content-between">
            <span className="badge bg-danger d-flex align-items-center gap-1">
              <span className="ss-live-dot" />
              LIVE
            </span>
            <span
              className="fw-semibold"
              style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.9rem' }}
            >
              <i className="fas fa-clock me-2 text-muted" style={{ fontSize: '0.75rem' }} />
              {elapsed}
            </span>
          </div>

          <StreamSidebarSection title="Session Info">
            <SidebarRow label="Stream Title" icon="fas fa-video">
              {activeSession.streamTitle || <span className="text-muted fst-italic">Untitled</span>}
            </SidebarRow>
            <SidebarRow label="Lab" icon="fas fa-flask">
              {activeSession.labName}
            </SidebarRow>
            <SidebarRow label="Presenter" icon="fas fa-user">
              {activeSession.presenterName}
            </SidebarRow>
            <SidebarRow label="Observers" icon="fas fa-eye">
              {activeSession.observersCount} observer
              {activeSession.observersCount !== 1 ? 's' : ''} watching
            </SidebarRow>
            <SidebarRow label="Started at" icon="fas fa-calendar-alt">
              {new Date(activeSession.startedAt).toLocaleString()}
            </SidebarRow>
          </StreamSidebarSection>

          {connectionInfo?.success && (
            <StreamSidebarSection title="Connection" defaultExpanded={false}>
              <SidebarRow label="WebSocket URL" icon="fas fa-server">
                {connectionInfo.wsUrl}
              </SidebarRow>
              <SidebarRow label="Room ID" icon="fas fa-door-open">
                {connectionInfo.roomId}
              </SidebarRow>
            </StreamSidebarSection>
          )}

          {videoInfo && (
            <StreamSidebarSection title="Video Stream" defaultExpanded={false}>
              <SidebarRow label="Source" icon="fas fa-video">
                {videoInfo.source === 'screen_share' ? 'Screen share' : 'Camera'}
              </SidebarRow>
              <SidebarRow label="Publisher" icon="fas fa-user-circle">
                {videoInfo.participantName || videoInfo.participantIdentity}
              </SidebarRow>
              <SidebarRow label="Resolution" icon="fas fa-expand-arrows-alt">
                {formatResolution(videoInfo)}
              </SidebarRow>
              <SidebarRow label="Codec" icon="fas fa-microchip">
                {videoInfo.mimeType || 'n/a'}
              </SidebarRow>
              <SidebarRow label="Bitrate" icon="fas fa-tachometer-alt">
                {formatBitrate(videoInfo.currentBitrate)}
              </SidebarRow>
              <SidebarRow label="Frames" icon="fas fa-film">
                {videoInfo.stats
                  ? `${videoInfo.stats.framesDecoded} decoded, ${videoInfo.stats.framesDropped} dropped`
                  : 'n/a'}
              </SidebarRow>
              <SidebarRow label="Packets" icon="fas fa-network-wired">
                {videoInfo.stats
                  ? `${videoInfo.stats.packetsReceived ?? 0} received, ${
                      videoInfo.stats.packetsLost ?? 0
                    } lost`
                  : 'n/a'}
              </SidebarRow>
              <SidebarRow label="Received" icon="fas fa-download">
                {formatBytes(videoInfo.stats?.bytesReceived)}
              </SidebarRow>
              <SidebarRow label="Track State" icon="fas fa-signal">
                {videoInfo.isMuted ? 'Muted' : videoInfo.streamState || 'n/a'}
              </SidebarRow>
            </StreamSidebarSection>
          )}

          {audioInfo && (
            <StreamSidebarSection title="Audio Stream" defaultExpanded={false}>
              <SidebarRow label="Source" icon="fas fa-volume-up">
                {audioInfo.source === 'screen_share_audio' ? 'Screen share audio' : 'Microphone'}
              </SidebarRow>
              <SidebarRow label="Publisher" icon="fas fa-user-circle">
                {audioInfo.participantName || audioInfo.participantIdentity}
              </SidebarRow>
              <SidebarRow label="Codec" icon="fas fa-microchip">
                {audioInfo.mimeType || 'n/a'}
              </SidebarRow>
              <SidebarRow label="Bitrate" icon="fas fa-tachometer-alt">
                {formatBitrate(audioInfo.currentBitrate)}
              </SidebarRow>
              <SidebarRow label="Audio Level" icon="fas fa-wave-square">
                {`${Math.round(audioInfo.audioLevel * 100)}%${
                  audioInfo.isSpeaking ? ' speaking' : ''
                }`}
              </SidebarRow>
              <SidebarRow label="Packets" icon="fas fa-network-wired">
                {audioInfo.stats
                  ? `${audioInfo.stats.packetsReceived ?? 0} received, ${
                      audioInfo.stats.packetsLost ?? 0
                    } lost`
                  : 'n/a'}
              </SidebarRow>
              <SidebarRow label="Received" icon="fas fa-download">
                {formatBytes(audioInfo.stats?.bytesReceived)}
              </SidebarRow>
              <SidebarRow label="Jitter" icon="fas fa-sliders-h">
                {formatMilliseconds(audioInfo.stats?.jitter)}
              </SidebarRow>
              <SidebarRow label="Concealment" icon="fas fa-headphones-alt">
                {audioInfo.stats
                  ? `${audioInfo.stats.concealmentEvents ?? 0} events, ${
                      audioInfo.stats.concealedSamples ?? 0
                    } samples`
                  : 'n/a'}
              </SidebarRow>
              <SidebarRow label="Track State" icon="fas fa-signal">
                {audioInfo.isMuted ? 'Muted' : audioInfo.isSubscribed ? 'Subscribed' : 'n/a'}
              </SidebarRow>
            </StreamSidebarSection>
          )}

          <StreamSidebarSection title="Technical IDs" defaultExpanded={false}>
            <TechId label="Session ID" value={activeSession.streamSessionId} />
            <TechId label="Device ID" value={activeSession.deviceId} />
            <TechId label="Lab ID" value={activeSession.labId} />
            <TechId label="Presenter ID" value={activeSession.presenterUserId} />
            {connectionInfo?.roomId && <TechId label="Room ID" value={connectionInfo.roomId} />}
            {videoInfo?.trackSid && <TechId label="Video Track ID" value={videoInfo.trackSid} />}
            {audioInfo?.trackSid && <TechId label="Audio Track ID" value={audioInfo.trackSid} />}
          </StreamSidebarSection>
        </div>
      </div>
    </div>
  );
};

export default StreamViewer;
